import React, { useState } from 'react';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import StatsGrid from './stats/StatsGrid';
import MaintenanceSection from './MaintenanceSection';
import UsageGrid from './UsageGrid';
import KpiVisibilityManager from './KpiVisibilityManager';
import { useDashboard } from '@/contexts/dashboard';
import { Button } from '@/components/ui/button';
import { Maximize2, Minimize2, Power, Thermometer, Fan } from 'lucide-react';
import FinancialsReportsGrid from './FinancialsReportsGrid';
import LoadControlGrid from './LoadControlGrid';
import HvacManagementGrid from './hvac-management/HvacManagementGrid';
import ChillerManagementGrid from './chiller-management/ChillerManagementGrid';

const CompactDashboard: React.FC = () => {
  const { kpiCards, isEditMode } = useDashboard();
  const [activeTab, setActiveTab] = useState('stats');
  const [expandedSection, setExpandedSection] = useState<string | null>(null);
  
  const visibleCards = kpiCards
    .filter(card => card.visible)
    .sort((a, b) => a.order - b.order);
  
  const toggleExpand = (section: string) => {
    if (expandedSection === section) {
      setExpandedSection(null);
    } else {
      setExpandedSection(section);
    }
  };
  
  return (
    <div className="h-full flex flex-col">
      <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full h-full flex flex-col">
        <div className="flex-none mb-2">
          <div className="flex justify-between items-center">
            <TabsList>
              <TabsTrigger value="stats">Stats</TabsTrigger>
              <TabsTrigger value="maintenance">Maintenance</TabsTrigger>
              <TabsTrigger value="financials">Financials & Reports</TabsTrigger>
              <TabsTrigger value="usage">Usage</TabsTrigger>
              <TabsTrigger value="loadControl">
                <Power className="mr-1 h-3 w-3" />
                Load Control
              </TabsTrigger>
              <TabsTrigger value="hvacManagement">
                <Thermometer className="mr-1 h-3 w-3" />
                HVAC
              </TabsTrigger>
              <TabsTrigger value="chillerManagement">
                <Fan className="mr-1 h-3 w-3" />
                Chillers
              </TabsTrigger>
            </TabsList>
            
            {isEditMode && <KpiVisibilityManager />}
          </div>
        </div>
        
        <div className="flex-1 overflow-hidden">
          <TabsContent value="stats" className="h-full overflow-hidden m-0">
            <div className="relative h-full overflow-hidden">
              <StatsGrid isEditMode={isEditMode} />
              <Button 
                variant="ghost" 
                size="icon" 
                className="absolute top-2 right-2" 
                onClick={() => toggleExpand('stats')}
              >
                {expandedSection === 'stats' ? <Minimize2 size={16} /> : <Maximize2 size={16} />}
              </Button>
            </div>
          </TabsContent>
          
          <TabsContent value="maintenance" className="h-full overflow-hidden m-0">
            <div className="relative h-full overflow-hidden bg-gradient-to-r from-blue-50 to-indigo-50 dark:from-blue-950/30 dark:to-indigo-950/30 rounded-xl border border-blue-100 dark:border-blue-900">
              <MaintenanceSection />
              <Button 
                variant="ghost" 
                size="icon" 
                className="absolute top-2 right-2" 
                onClick={() => toggleExpand('maintenance')}
              >
                {expandedSection === 'maintenance' ? <Minimize2 size={16} /> : <Maximize2 size={16} />}
              </Button>
            </div>
          </TabsContent>
          
          <TabsContent value="financials" className="h-full overflow-hidden m-0">
            <div className="relative h-full overflow-auto">
              <FinancialsReportsGrid />
              <Button 
                variant="ghost" 
                size="icon" 
                className="absolute top-2 right-2" 
                onClick={() => toggleExpand('financials')}
              >
                {expandedSection === 'financials' ? <Minimize2 size={16} /> : <Maximize2 size={16} />}
              </Button>
            </div>
          </TabsContent>
          
          <TabsContent value="usage" className="h-full overflow-hidden m-0">
            <div className="relative h-full overflow-auto">
              <UsageGrid />
              <Button 
                variant="ghost" 
                size="icon" 
                className="absolute top-2 right-2" 
                onClick={() => toggleExpand('usage')}
              >
                {expandedSection === 'usage' ? <Minimize2 size={16} /> : <Maximize2 size={16} />}
              </Button>
            </div>
          </TabsContent>
          
          <TabsContent value="loadControl" className="h-full overflow-hidden m-0">
            <div className="relative h-full overflow-auto">
              <LoadControlGrid />
              <Button 
                variant="ghost" 
                size="icon" 
                className="absolute top-2 right-2" 
                onClick={() => toggleExpand('loadControl')}
              >
                {expandedSection === 'loadControl' ? <Minimize2 size={16} /> : <Maximize2 size={16} />}
              </Button>
            </div>
          </TabsContent>
          
          <TabsContent value="hvacManagement" className="h-full overflow-hidden m-0">
            <div className="relative h-full overflow-auto">
              <HvacManagementGrid />
              <Button 
                variant="ghost" 
                size="icon" 
                className="absolute top-2 right-2" 
                onClick={() => toggleExpand('hvacManagement')}
              >
                {expandedSection === 'hvacManagement' ? <Minimize2 size={16} /> : <Maximize2 size={16} />}
              </Button>
            </div>
          </TabsContent>
          
          <TabsContent value="chillerManagement" className="h-full overflow-hidden m-0">
            <div className="relative h-full overflow-auto">
              <ChillerManagementGrid />
              <Button 
                variant="ghost" 
                size="icon" 
                className="absolute top-2 right-2" 
                onClick={() => toggleExpand('chillerManagement')}
              >
                {expandedSection === 'chillerManagement' ? <Minimize2 size={16} /> : <Maximize2 size={16} />}
              </Button>
            </div>
          </TabsContent>
        </div>
      </Tabs>
    </div>
  );
};

export default CompactDashboard;
